import React from "react";
import { Box, Container, Typography, Button, keyframes } from "@mui/material";
import { useTheme } from "@emotion/react";
import { Link as RouterLink } from "react-router-dom";
import { getPageRoute } from "src/services";
import PodcastBadge from "./PodcastBadge.js";

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
`;

const stats = [
  { num: "200-400%", label: "of national growth norm", accent: true },
  { num: "2-8 hrs", label: "given back per week" },
];

const HomeHero = () => {
  const theme = useTheme();

  return (
    <Box
      component="section"
      sx={{
        py: { xs: 8, md: 12 },
        position: "relative",
        overflow: "hidden",
        bgcolor: theme.palette.paper.primary,
      }}
    >
      {/* Soft blob accents */}
      <Box
        sx={{
          position: "absolute",
          top: "-50px",
          right: "-100px",
          width: 480,
          height: 480,
          borderRadius: "50%",
          bgcolor: "#dcfce7",
          opacity: 0.7,
          filter: "blur(30px)",
          zIndex: 0,
        }}
      />
      <Box
        sx={{
          position: "absolute",
          bottom: "-150px",
          left: "-80px",
          width: 340,
          height: 340,
          borderRadius: "50%",
          bgcolor: "#ffedd5",
          opacity: 0.5,
          filter: "blur(30px)",
          zIndex: 0,
        }}
      />

      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        {/* Kicker */}
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 1,
            fontSize: 12,
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            color: "#14532d",
            fontWeight: 700,
            px: 2,
            py: 1,
            bgcolor: "#dcfce7",
            borderRadius: 999,
            mb: 4,
          }}
        >
          <Box
            component="span"
            sx={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              bgcolor: theme.palette.success.main,
              animation: `${pulse} 2s infinite`,
            }}
          />
          AI + Coaching for K-12 STEM Teachers
        </Box>

        <Typography
          component="h1"
          sx={{
            fontSize: { xs: "2.75rem", md: "4.75rem" },
            fontWeight: 800,
            letterSpacing: "-0.035em",
            lineHeight: 1.02,
            color: theme.palette.text.primary,
            maxWidth: 900,
          }}
        >
          Give teachers back their time, so they can focus on the{" "}
          <Box component="span" sx={{ color: theme.palette.success.main }}>
            thinking behind teaching
          </Box>
          .
        </Typography>

        <Typography
          sx={{
            fontSize: { xs: "1.05rem", md: "1.3125rem" },
            color: theme.palette.text.secondary,
            mt: 4,
            maxWidth: 620,
            lineHeight: 1.5,
          }}
        >
          Maestro pairs every K-12 STEM teacher with a personalized AI workflow
          and a weekly coach, so the 60% of the week that isn't teaching stops
          eating the 40% that is.
        </Typography>

        <Box
          sx={{
            mt: 5,
            display: "flex",
            gap: 2,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <Button
            variant="contained"
            size="large"
            component={RouterLink}
            to={getPageRoute("waitlist", "WAITLIST")}
            sx={{
              bgcolor: theme.palette.text.primary,
              color: theme.palette.paper.primary,
              px: 3.5,
              py: 1.75,
              fontSize: "0.9375rem",
              fontWeight: 600,
              textTransform: "none",
              borderRadius: 999,
              "&:hover": {
                bgcolor: theme.palette.text.primary,
                transform: "translateY(-2px)",
              },
              transition: "transform 0.15s",
            }}
          >
            Join the waitlist →
          </Button>
          <Button
            href="#how"
            sx={{
              color: theme.palette.text.primary,
              px: 0,
              py: 1.75,
              fontSize: "0.9375rem",
              fontWeight: 600,
              textTransform: "none",
              "&:hover": {
                bgcolor: "transparent",
                color: theme.palette.success.main,
              },
            }}
          >
            See how it works →
          </Button>
        </Box>

        <Box sx={{ mt: { xs: 4, md: 5 } }}>
          <PodcastBadge />
        </Box>

        <Box
          sx={{
            mt: { xs: 6, md: 8.5 },
            pt: 4,
            borderTop: `1px solid ${theme.palette.text.tertiary}`,
            display: "grid",
            gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(2, 1fr)" },
            gap: { xs: 3, md: 5 },
            maxWidth: 720,
          }}
        >
          {stats.map((s) => (
            <Box key={s.label}>
              <Typography
                sx={{
                  fontSize: { xs: "1.5rem", md: "2rem" },
                  fontWeight: 700,
                  letterSpacing: "-0.02em",
                  color: s.accent
                    ? theme.palette.success.main
                    : theme.palette.text.primary,
                }}
              >
                {s.num}
              </Typography>
              <Typography
                sx={{
                  fontSize: "0.8125rem",
                  color: theme.palette.text.secondary,
                  mt: 0.5,
                }}
              >
                {s.label}
              </Typography>
            </Box>
          ))}
        </Box>
      </Container>
    </Box>
  );
};

export default HomeHero;
